// `chrome-extension.extension-tabs.shortlist.hunter.review` (plan §7 phase 3).
//
// Upstream's view is two children of `.shortlist-hunter-review`:
//
//   <shortlist-extension-alerts … force-slim="::true" ng-style="flex-basis: Slim">
//   <div ui-view="shortlist-hunter-review-side-view" class="…" ng-class="{ rendered: … }">
//
// ⚠️ SLICE 2 PORTS THE TOP OF THE FIRST CHILD, not the whole of it.
// `shortlist-extension-alerts` is 709 lines of controller over a 210-line template with six
// mutually exclusive top-level branches (loading, no-alerts-full, no-alerts-slim, the
// department picker, the content container, and the notification wrapper). What renders here
// is the slim content container's first row — the alert selector — because that is what slice
// 2 is: the hunter list, which in the only mode the extension ever uses collapses to one row.
//
// The candidate list under it, the ATS-source dropdown beside it and the automation banner are
// slice 3's; the criteria builder behind the pen is the rest of slice 2. Each is left out
// rather than stubbed, for the reason `extension.tsx` gives: an invented placeholder lands in
// the middle of every pixel diff and has to be removed again.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactElement } from 'react'
import { useLocation, useNavigate, useOutlet, useSearchParams } from 'react-router-dom'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  AlertEntity,
  fetchViewModel,
  getAlerts,
  favorCandidates,
  unfavorCandidate,
  getCandidateDetailsByAlertId,
  getCandidatesByAlertId,
  hideCandidates,
  unhideCandidate,
  getExternalSourceConfiguration,
  getBeautifiedCriteria,
  getCandidateNumbersByAlertId,
  getExternalSourceCandidates,
  type ExternalSourceCandidate,
  deleteAlertById,
  enableAutomation,
  HunterAutomationMode,
  setAutomationMode,
  getCandidateEmailIntegrationDetails,
  downloadWooProfile,
  NEW_ALERT_ID,
  searchExtensionJobs,
  convertJobToAlert,
  getUserDepartments,
  updateUserDepartments,
  type UserDepartment,
  type ExtensionAlert,
  type AlertCriteriaData,
  type ExtensionAlertContainer,
  type ExtensionJobSearchResponse,
  type ExternalSourceConfigurationResponse,
  type AlertCandidate,
  sendCandidateDetails,
  type AlertCandidateDetails,
} from '@woo-ext/api'
import {
  AlertContainerHost,
  bulkActions,
  SORT_OPTIONS,
  timeSince,
  LogicalOperator,
  ActivateAutomationDialogue,
  DeleteAlertDialogue,
  MultiSelectModal,
  ExportToAtsModal,
  ExportToCsvModal,
  NoValidCandidatesDialogue,
  ProfileActionConfirmationModal,
  CANDIDATE_DETAILS_IN_PROGRESS_MESSAGE,
  PROFILE_ACTION_OK_BUTTON_TEXT,
  candidateDetailsResultMessage,
  FriendlyCriteriaPanel,
  ProfileAction,
  applyFavor,
  applyUnfavor,
  applyUnhide,
  addToJobTitle,
  exportTitle,
  profileActions,
  type ProfileActionDefinition,
  applyHide,
  applyToIds,
  archivedRowsStay,
  nextRowAfterArchive,
  withoutRows,
  candidateName,
  type BulkAction,
  type BulkActionFlags,
  CandidateListHost,
  CandidateFiltersBar,
  CandidateFiltersTooltip,
  CandidatePagination,
  CandidateRow,
  MessageHistoryTooltip,
  useMessageHistory,
  CandidateSelectionBar,
  ExtensionAlertsHost,
  NoAlertsSlim,
  hasValidIdsForMessagingOrEmpty,
  saveFile,
  sortNoneOfTheAboveLast,
  CV_DATE_OPTION_DEFAULT,
  HunterAutomationBanner,
  SHOWN_OPTION_DEFAULT,
  buildCandidateFilter,
  cvDateOptionForAlert,
  shownOptionForAlert,
  shownOptionsFor,
  sortOptionForAlert,
  type SortOption,
  SlimHeaderDropdown,
  SlimHeaderDropdownPanel,
  SourcesDropdown,
  type SlimCandidate,
  useDismissOnOutsideClick,
  sourceOptions,
  type SocialHost,
  getAtsLabel,
  singleProfileExportEnabled,
} from '@woo-ext/ui'
import { inheritedData, inheritedString } from './routeData.ts'
import { trackCandidate, trackCandidateBulk, trackCandidateList, trackCandidateSelection } from './actionTracking.ts'
import { sendAssignToJob, sendOpenHotlist } from './assignToJob.ts'
import { useShortlistExportRequests, useShortlistFlagEvents, useShortlistMessageRequests } from './shortlistEvents.ts'
import {
  clearCheckedEntities,
  getCheckedEntities,
  getCheckedVersion,
  setCheckedEntities,
  subscribeToCheckedEntities,
  type CheckedEntity,
} from './entityCheckboxContainer.ts'
import {
  ExtensionProduct,
  candidateAlertFieldsFromParams,
  getUnexpiredLastAlertViewed,
  isRestorable,
  persistLastAlertViewed,
  restoredPage,
  restoredSortBy,
  scrollToRestoredRow,
  sessionUserIdOf,
  type ExtensionProductLastAlertViewedState,
} from './lastAlertViewed.ts'

/**
 * `window.careero.featureFlags[AUTOMATED_HUNTERS]` (entity-hunters-container-base.ng1.ts:260).
 *
 * ⚠️ READ, NOT ASSUMED. It selects between two entirely different dropdown panels, and the
 * value is tenant configuration — the dev tenant shows an "Activate automation" banner on this
 * screen, production tenants need not. Defaulting either way would render one tenant's panel
 * confidently on the other's data.
 */
const AUTOMATED_HUNTERS = 'AUTOMATED_HUNTERS'

/**
 * `shortlistHunterAutomationService.isEnabled` — `featureFlags[HUNTER_AUTOMATION] &&
 * careero.hunterAutomationEnabled`.
 *
 * ⚠️ NOT `AUTOMATED_HUNTERS`, WHICH THIS SCREEN WAS USING FOR BOTH. That one gates the
 * dropdown's job-search group and is combined with the product; this one gates the automation
 * BANNER and the dot beside every search name, and is product-independent. They happen to
 * agree on the dev tenant, which is exactly how one stood in for the other unnoticed.
 */
const HUNTER_AUTOMATION = 'HUNTER_AUTOMATION'

/**
 * `shortlist.candidate-message.service.ng1.ts:186` — the envelope and its "Never" on every row
 * are behind this one flag, and nothing else on the screen reads it.
 */
const SHORTLIST_MESSAGE_CANDIDATES = 'SHORTLIST_MESSAGE_CANDIDATES'

/**
 * The bulk bar's flags. Each pairs a feature flag with a view-model boolean — see
 * `BulkActionFlags`, which states the four expressions.
 */
const ADD_TO_JOB = 'ADD_TO_JOB'
const ASSIGN_TO_JOB = 'ASSIGN_TO_JOB'
const EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED = 'EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED'
const SEND_CANDIDATE_DETAILS_TO_USER = 'SEND_CANDIDATE_DETAILS_TO_USER'

const EXPORT_CANDIDATES = 'EXPORT_CANDIDATES'
/** `ShortlistActionedAndUnactionedView` — adds the "All + Actioned" option at index 1. */
const SHORTLIST_ACTIONED_AND_UNACTIONED_VIEW = 'SHORTLIST_ACTIONED_AND_UNACTIONED_VIEW'
const EXPORT_TO_FILE_DISABLED = 'EXPORT_TO_FILE_DISABLED'

/** Which alert the route is on — `?alertId`, the only param this screen needs today. */
function useAlertId(): number | undefined {
  const [params] = useSearchParams()
  const raw = params.get('alertId')
  if (raw === null) return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export interface ShortlistReviewProps {
  /** Candidates here; Phase 4 passes ClientContacts and reuses everything else. */
  entity?: AlertEntity
}

/**
 * `private readonly pageSize: number = 10` — `extension-alert-candidates.component.ng1.ts:244`.
 *
 * The list endpoint takes no paging parameters: it answers with every match and the client
 * slices. So this constant is the only thing standing between a 200px panel and the whole
 * alert in the DOM.
 */
const CANDIDATE_PAGE_SIZE = 10

/**
 * The row whose preview the list opened (`selectedCandidateId` / `selectedClientContactId`).
 * Module state rather than a ref because opening a side panel REMOUNTS the review, and the value
 * has to outlive that — the same reason the selection lives in `entityCheckboxContainer`.
 *
 * An archive advances to the next row only from this one (`previouslySelectedCandidateId ===
 * candidateIds[0]`); a preview opened by URL has none, and upstream does not advance from it.
 */
const selectedRow: { current: number | undefined } = { current: undefined }

/** The last search `HunterResultsLoaded` was recorded for, in this document. */
let lastRecordedSearch: string | undefined

/**
 * `sendNewSearchMadeAction` (component:2253) — `HunterResultsLoaded`, once per `startNewSearch()`.
 *
 * ⚠️ IT NEEDS THE DETAILS CALL, WHICH THIS SCREEN DOES NOT OTHERWISE MAKE ON LOAD. Upstream's
 * search is `getCandidateDetailsByAlertId` first and the page second, and the event reads
 * `totalEnriched` off the first — a field the list endpoint does not carry (measured: the list
 * answers `candidatePage`, `allRelevantIds`, `maxResults` and nothing else). So the same query
 * goes out a second time, WITHOUT `persistFilterAndSort` — the list call already carries the
 * write.
 *
 * ⚠️ ONCE PER QUERY PER DOCUMENT, NOT PER MOUNT. Upstream does not search again when the list
 * comes back: `reloadStateIfNeeded` restores the last search from storage (same alert, unexpired)
 * and records nothing, so opening a row, a panel or the composer and returning is silent there.
 * This screen remounts on each of those and now restores the same way (`lastAlertViewed.ts`), so
 * a restore never reaches here; the key is the backstop for the remount that has nothing to
 * restore — an empty result, whose state upstream removes rather than saves. `force` is the
 * other direction: a filter or sort picked again is a new search upstream even though the query
 * has not changed.
 */
function recordCandidateSearch(
  alertId: number,
  sortBy: CandidateSearchQuery['sortBy'],
  filter: CandidateSearchQuery['filter'],
  force = false,
): void {
  const key = JSON.stringify([alertId, sortBy, filter])
  if (!force && key === lastRecordedSearch) return
  lastRecordedSearch = key
  getCandidateDetailsByAlertId({ alertId, sortBy, filter }).then(
    (details) => {
      trackCandidateList.resultsLoaded(alertId, details)
    },
    () => undefined,
  )
}

type CandidateSearchQuery = Parameters<typeof getCandidateDetailsByAlertId>[0]

/** `MAX_RESULTS = 1000` — "this limit is also on the server side" (component:243). */
const CANDIDATE_MAX_RESULTS = 1000

export function ShortlistReview({ entity = AlertEntity.Candidates }: ShortlistReviewProps): ReactElement {
  const alertId = useAlertId()
  const navigate = useNavigate()
  const [routeParams] = useSearchParams()

  const { pathname } = useLocation()

  /**
   * `?candidateId` — which row currently has its preview open, so the row can mark itself and
   * a second click can close it.
   *
   * ⚠️ THE PARAM ALONE IS NOT "OPEN", AND READING IT ALONE BREAKS THE FIRST CLICK.
   * `isPreviewShown` (extension-alert-candidates.component.ng1.ts:1611) is three conditions,
   * not one: the param, `$state.includes(HunterReviewCandidateProfile)` — the profile CHILD
   * must be active — and the id matching. The frame is routinely entered at the bare review
   * state with a stale `?candidateId` still on the URL (upstream's own `navigateToCandidate`
   * contemplates exactly that), and treating that as "already open" makes the row's first
   * click CLOSE a preview that was never drawn: measured against build 354, which opened the
   * profile from the same URL.
   */
  const previewRouteActive = pathname.includes('/review/candidate/profile')
  const openCandidateRaw = routeParams.get('candidateId')
  const openCandidateId =
    !previewRouteActive || openCandidateRaw === null ? undefined : Number.parseInt(openCandidateRaw, 10)
  /** For the list's fetch, which must not refetch because a preview opened. */
  const openCandidateRef = useRef(openCandidateId)
  openCandidateRef.current = openCandidateId

  /**
   * `openCandidateSnippet` (extension-alert-candidates.component.ng1.ts:1596) — and it is a
   * TOGGLE, not a navigation.
   *
   * ⚠️ CLICKING THE OPEN CANDIDATE CLOSES IT. Upstream returns early through
   * `closePreviewResume()` when `isPreviewShown(candidate.id)`, so the row is how the preview
   * is dismissed as well as opened. A handler that always navigates leaves the user no way
   * back to the plain list.
   *
   * ⚠️ AND THE TARGET IS `/more`, NOT THE BARE PROFILE. `snippetState` is
   * `HunterReviewCandidateProfileMore`; the profile route exists and is a different screen.
   * `algoScore` travels with it because the destination reads it — without it the profile's
   * "view more" state is false and it renders closed.
   */
  const openCandidate = (candidateId: number, algorithm?: number): void => {
    // ⚠️ THE SELECTED ALERT, NOT `?alertId`. Upstream sends `this.extensionAlert.id` — the
    // alert the user is looking at. The frame is routinely opened with no `?alertId` at all
    // (the shortlist picks the first alert itself), and reading the param instead produced
    // `alertId=` empty in the URL, which the profile cannot resolve. `current` is declared
    // below; this only reads it on a click, long after it is initialised.
    const forAlert = current?.alert.id ?? alertId
    if (forAlert === undefined) return
    const base = '/chrome-extension/extension/shortlist/review/candidate'
    if (openCandidateId === candidateId) {
      selectedRow.current = undefined
      void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(forAlert)}`)
      return
    }
    selectedRow.current = candidateId
    // `persistState(candidate.id, true)` — the row is the selection a reload comes back to.
    persistListState(candidateId)
    // ⚠️ THE QUERY IS UPSTREAM'S STRING, ORDER AND DEFAULT INCLUDED, because it is the page
    // view's URL. ui-router 0.2 writes the deepest state's params first and keeps a defaulted
    // one: measured, a row click there lands on
    // `…/profile/more?originSnippet=0&candidateId=…&algoScore=…&alertId=…` — `originSnippet` is
    // the profile's bool param at its default, then the candidate state's two, then the alert.
    const query = new URLSearchParams({ originSnippet: '0', candidateId: String(candidateId) })
    if (algorithm !== undefined) query.set('algoScore', String(algorithm))
    query.set('alertId', String(forAlert))
    void navigate(`${base}/profile/more?${query.toString()}`)
  }

  // ⚠️ THE FLAG IS DECLARED BY THE CHILD AND READ BY THE PARENT, which is why it comes from the
  // ROUTE DATA rather than from a prop. `isCheckboxDependent: true` sits on `review/export`,
  // `review/add-to-job` and `review/candidate-messages` — the three panels that act on a
  // SELECTION — and not on `review/email-signin`, which acts on the user's mailbox. So while one
  // of those owns the frame the list's own controls are disabled and titled "Please close the
  // <label> window to continue", and while the sign-in panel owns it they are not. Reading it
  // off the open panel instead of off the route would need a second list of which panels count;
  // `inheritedData` already resolves the deepest declaring route, which IS the open one.
  const checkboxDependent = inheritedData(pathname, 'isCheckboxDependent') === true
  const checkboxOperationLabel = inheritedString(pathname, 'checkboxOperationLabel')
  const [containers, setContainers] = useState<ExtensionAlertContainer[] | undefined>(undefined)
  const [failed, setFailed] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [automatedHunters, setAutomatedHunters] = useState<boolean | undefined>(undefined)
  const [automationEnabled, setAutomationEnabled] = useState(false)
  const [jobs, setJobs] = useState<ExtensionJobSearchResponse | undefined>(undefined)
  /** `data.allRelevantDetails`, filled by "select all results". */
  const [allDetails, setAllDetails] = useState<readonly AlertCandidateDetails[]>([])
  /** `showNoValidCandidatesDialogue()`. */
  const [noValidCandidatesOpen, setNoValidCandidatesOpen] = useState(false)
  /** The bulk action waiting on `verifyHasSelectedCandidates`' "Select candidates" modal. */
  const [multiSelectAction, setMultiSelectAction] = useState<BulkAction | undefined>(undefined)
  /** The ids an open ATS export / CSV export dialogue is exporting. */
  const [exportingToAts, setExportingToAts] = useState<readonly number[] | undefined>(undefined)
  const [exportingToCsv, setExportingToCsv] = useState<readonly number[] | undefined>(undefined)
  /** The candidate whose "Email candidate details to me" modal is open. */
  const [sendingDetailsFor, setSendingDetailsFor] = useState<number | undefined>(undefined)
  // `userDepartments` — read only for the empty state, which is the one place that uses them.
  const [departments, setDepartments] = useState<UserDepartment[] | undefined>(undefined)
  const [searching, setSearching] = useState(false)
  /** Monotonic id for the job search, so a slow response cannot overwrite a newer one. */
  const jobSearchSeq = useRef(0)
  const [sources, setSources] = useState<ExternalSourceConfigurationResponse | undefined>(undefined)
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  /** `getAtsLabel($window)` — what the screen SAYS where `atsName` is the key (5cfd3ef899). */
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [messagingEnabled, setMessagingEnabled] = useState(false)
  // The envelope's Message History — `openCandidateMessagesTooltip`.
  const messageHistory = useMessageHistory('candidates')
  /**
   * ⚠️ TWO EXPORT FLAGS, TWO DIFFERENT ACTIONS BEHIND ONE ICON.
   * `exportCandidatesToExistingListEnabled` opens the `…/review/export` panel
   * (`goToExportCandidatesState`); `exportCandidatesEnabled` alone pops a modal and POSTs the
   * list straight into the ATS. `bulkActions` folds both into one entry because the BAR is the
   * same either way — the click is not, so the variant is kept here.
   */
  const [exportToExistingList, setExportToExistingList] = useState(false)
  const [actionFlags, setActionFlags] = useState<BulkActionFlags>({})
  const [showAlgorithm, setShowAlgorithm] = useState(false)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [candidates, setCandidates] = useState<AlertCandidate[] | undefined>(undefined)
  // `maxResults` is the TOTAL behind the alert, not the page length — it is what the count
  // beside the user icon shows, and the page is a slice of it.
  const [totalResults, setTotalResults] = useState<number | undefined>(undefined)
  /**
   * The automation confirmation, and the only route to `enableAutomation`.
   *
   * ⚠️ THE FLAG EXISTS SO THE BUTTON CANNOT REACH THE API DIRECTLY. `enableAutomation` messages
   * every matching candidate for the alert and keeps messaging new ones; upstream gates it
   * behind `showGenericDialogue` and so does this. Nothing else sets it.
   */
  const [activatingAutomation, setActivatingAutomation] = useState(false)

  // ⚠️ THE BAR'S THREE VALUES ARE SEEDED FROM THE ALERT, NOT FROM THE DEFAULTS, and that is a
  // correctness fix rather than a feature: the server PERSISTS them per alert
  // (`filterShowHiddenCandidates` and its five siblings, plus `sortBy`), so an alert the
  // recruiter last left on "Archived" came back showing every candidate under a bar that said
  // "All". `syncFiltersAndSorting` is the upstream call this reproduces, and it runs before
  // the first fetch for the same reason.
  const [sortOption, setSortOption] = useState<SortOption>(() => sortOptionForAlert(undefined))
  const [shownOption, setShownOption] = useState(() => shownOptionForAlert({}))
  const [cvDateOption, setCvDateOption] = useState(() => cvDateOptionForAlert(undefined))
  // Distinguishes "the user picked this" from "the alert arrived like this" — only the first
  // may be written back. See the fetch effect.
  const [sortChosen, setSortChosen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)

  /**
   * ⚠️ A SET OF IDS, HELD HERE, BECAUSE THE SIDE VIEW NEEDS IT TOO. Upstream resolves an
   * `EntityCheckboxContainer` onto the review state and the profile reads it from there —
   * `getCheckedCandidateIds()` is what the bulk dialogs list. State on the row would make a
   * selection the profile cannot see; state in a context provider would be the same thing
   * with more ceremony, since the outlet already carries the alert's name.
   *
   * ⚠️ AND IT IS NOT RESET BY A SORT. Upstream only resets on `resetCandidatesCheckbox` —
   * "Clear all" and a single action that consumed the selection. Sorting the same result set
   * keeps the same people selected, which is what a recruiter sorting to find one of them
   * expects.
   */
  /**
   * ⚠️ NOT `useState`, AND THE REASON IS MEASURED — see `entityCheckboxContainer.ts`. Entering
   * a side-view child unmounts and remounts this screen, so component state cannot survive a
   * bulk action long enough to reach the panel that action opens.
   */
  const checkedVersion = useSyncExternalStore(subscribeToCheckedEntities, getCheckedVersion)
  const [selectMenuOpen, setSelectMenuOpen] = useState(false)
  /** `ctrl.isBulkActionsMoreOpen` — the ⋮ beside the inline bulk actions. */
  const [bulkOverflowOpen, setBulkOverflowOpen] = useState(false)
  /** `ctrl.currPage` — 1-based, as upstream counts it. */
  const [currentPage, setCurrentPage] = useState(1)
  /**
   * `extensionAuthService.getExtensionUserSession().userId` — `undefined` until the view model
   * answers, `null` when it could not. `reloadStateIfNeeded` refuses another user's state, so
   * the restore decision waits for it.
   */
  const [sessionUserId, setSessionUserId] = useState<number | null | undefined>(undefined)
  /**
   * `reloadStateIfNeeded`'s verdict for the alert on screen, taken once per alert per mount:
   * `restore` is the stored search being replayed, `null` a new search. Nothing is fetched until
   * it exists — fetching first and restoring second would be a new search, and record one.
   */
  const [seed, setSeed] = useState<
    { alertId: number; restore: ExtensionProductLastAlertViewedState | null } | undefined
  >(undefined)
  /** The ☰'s own menu, which is NOT the alert list — see the call site. */
  const [slimActionsOpen, setSlimActionsOpen] = useState(false)
  /**
   * `ctrl.profileActions` — the six icons the row reveals on hover.
   *
   * Built from the same bootstrap `actionFlags` comes from, but kept separate: that one is
   * the BULK contract (`bulkActions`) and this is the per-candidate one, and the two take
   * different flags even where the names rhyme.
   */
  const [rowActions, setRowActions] = useState<readonly ProfileActionDefinition[]>([])
  // `collapsibleFiltersState` — the tooltip and its two groups, all starting closed.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [groupOpen, setGroupOpen] = useState(false)
  const [cvDateOpen, setCvDateOpen] = useState(false)

  // ⚠️ SIX MENUS, SIX SUBSCRIPTIONS, AND THE TRIGGER IS ALWAYS IN THE `within` LIST. Upstream
  // gets this from two places at once — `uibDropdownService` closes any open `uib-dropdown` on
  // a document click, and the tooltip menus declare `on-click-outside` themselves — and the
  // port had neither, so every one of these stayed open until its own trigger was clicked
  // again. Each list names the menu AND the control that opens it: omitting the trigger makes
  // the closing click re-open the menu from the trigger's own handler. `.tooltip` is upstream's
  // `click-outside-additional`, and it covers the panels that render as tooltip chrome.
  useDismissOnOutsideClick({
    open: panelOpen,
    onDismiss: () => { setPanelOpen(false) },
    within: ['.shortlist-header-slim-alert-name-hover-ctas', '.tooltip'],
  })
  useDismissOnOutsideClick({
    open: slimActionsOpen,
    onDismiss: () => { setSlimActionsOpen(false) },
    within: ['.more-options-caret'],
  })
  useDismissOnOutsideClick({
    open: sourcesOpen,
    onDismiss: () => { setSourcesOpen(false) },
    within: ['.whitelabel-sources-dropdown-container'],
  })
  useDismissOnOutsideClick({
    open: sortOpen,
    onDismiss: () => { setSortOpen(false) },
    within: ['.slim-filters-action'],
  })
  // `onFiltersTooltipClose()`: closes the menu AND folds both groups, so the next open shows the
  // current filters with no list expanded. It runs on a click outside and on picking an option
  // (`changeShownCandidates`, `changeRelevancyDateFilter`); the trigger's own toggle
  // (`onFiltersTooltipToggle`) only flips the menu and leaves the groups as they were. The
  // contacts screen's copy has the reset commented out, which is why that one keeps its state.
  const closeFiltersMenu = (): void => {
    setFiltersOpen(false)
    setGroupOpen(false)
    setCvDateOpen(false)
  }
  useDismissOnOutsideClick({
    open: filtersOpen,
    onDismiss: closeFiltersMenu,
    within: ['.slim-filters-action', '.tooltip'],
  })
  useDismissOnOutsideClick({
    open: selectMenuOpen,
    onDismiss: () => { setSelectMenuOpen(false) },
    within: ['.candidate-selection-container'],
  })
  useDismissOnOutsideClick({
    open: bulkOverflowOpen,
    onDismiss: () => { setBulkOverflowOpen(false) },
    within: ['.candidate-selection-bulk-actions-more'],
  })
  // Marks a filter the USER chose, so it is written back; the same distinction `sortChosen`
  // draws for the sort. See the fetch effect.
  const [filtersChosen, setFiltersChosen] = useState(false)
  const [actionedViewEnabled, setActionedViewEnabled] = useState(false)

  // The alert this list belongs to, which has to be resolved BEFORE the early returns below
  // because the side view needs it.
  // ⚠️ THE LIST IS PAGINATED CLIENT-SIDE, AND NOT DOING IT RENDERED THE WHOLE ALERT.
  // `getCandidatesByAlertId` takes no paging parameters — upstream holds the full result and
  // slices it (`setPageResults`, component:1359) — so a port that simply maps over the
  // response puts every match in the DOM. Measured on Bullhorn against build 354: prod had
  // 10 rows and 896 elements in the panel, this had 433 rows and 9,594.
  const current = containers?.find((container) => container.alert.id === alertId) ?? containers?.[0]

  /**
   * The selection, read out of the container now that the alert is known. `checkedVersion` is
   * in the dependency list rather than the value: the store hands out the same array instance
   * until something writes, and the version is what says it did.
   */
  const selectedAlertId = current?.alert.id
  const checked = useMemo(
    () => getCheckedEntities(selectedAlertId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkedVersion IS the dependency
    [selectedAlertId, checkedVersion],
  )
  const selectedIds = useMemo(() => new Set(checked.map((entry) => entry.id)), [checked])

  /**
   * The ids the list holds, as `getAllRelevantIdsFromCandidateDetails()` answers them. A ref so
   * that an archive can shorten it BEFORE the next row's selection is persisted — the state
   * update lands a render later, after both writes.
   */
  const listIdsRef = useRef<readonly number[]>([])
  listIdsRef.current = (candidates ?? []).map((candidate) => candidate.id)

  /**
   * `persistState(candidateId, …)` (component:1663) — how the list was left: the query, the page,
   * every id, and the row. Nothing is written before the list has been seeded, and an empty list
   * REMOVES the entry ("so it will try to start a new search upon refresh").
   */
  const persistListState = (selectedId: number | undefined, page: number = currentPage): void => {
    const alert = current?.alert
    if (alert === undefined || seed?.alertId !== alert.id || candidates === undefined) return
    void persistLastAlertViewed(ExtensionProduct.Candidates, {
      ...(typeof sessionUserId === 'number' ? { userId: sessionUserId } : {}),
      selectedAlertId: alert.id,
      ...(selectedId === undefined ? {} : { selectedEntityId: selectedId }),
      allEntityIds: [...listIdsRef.current],
      selectedPage: page,
      getDataParams: { alertId: alert.id, sortBy: sortOption.field, filter: { ...buildCandidateFilter(shownOption, cvDateOption.value) } },
    })
  }

  /**
   * Written back through the container, so it survives the remount that opening a panel
   * causes. The NAME travels with the id because the panels title themselves with it and have
   * no list to look it up in.
   */
  const setSelectedIds = (
    update: ReadonlySet<number> | ((previous: ReadonlySet<number>) => ReadonlySet<number>),
  ): void => {
    if (selectedAlertId === undefined) return
    const next = typeof update === 'function' ? update(selectedIds) : update
    const known = new Map<number, CheckedEntity>(checked.map((entry) => [entry.id, entry]))
    setCheckedEntities(
      selectedAlertId,
      [...next].map((id) => {
        const already = known.get(id)
        if (already !== undefined) return already
        const row = candidates?.find((candidate) => candidate.id === id)
        return { id, name: row === undefined ? undefined : candidateName(row) }
      }),
    )
  }

  /**
   * `?externalSource` — the list behind a source that is NOT the tenant's own ATS.
   *
   * ⚠️ A DIFFERENT ENDPOINT AND A DIFFERENT DTO. `external-source/candidate-search/
   * getCandidateDetailsByAlertId` answers the SOURCE's candidates, whose ids are external
   * strings and which carry `exposed`/`addedToAts` instead of the alert flags. Before this the
   * source dropdown had no `onSelect` at all, so picking Monster did nothing; wiring only the
   * navigation would have been worse, because this screen would then have drawn the ATS list
   * under Monster's name.
   *
   * ⚠️ AND `source` IS THE CONFIGURATION'S `hostSite` STRING, CASED AS IT COMES — "Monster",
   * not "MONSTER". The server's enum rejects the upper-case spelling with a 400 naming all
   * thirty-odd accepted values, which is how this was pinned down.
   */
  const externalSource = routeParams.get('externalSource')
  const [externalCandidates, setExternalCandidates] = useState<ExternalSourceCandidate[] | undefined>(undefined)
  // `currentData.maxResults`: the server's total for the source, which the pager's `ng-if` reads.
  const [externalMaxResults, setExternalMaxResults] = useState(0)
  useEffect(() => {
    const alert = current?.alert
    if (externalSource === null || alert === undefined) {
      setExternalCandidates(undefined)
      setExternalMaxResults(0)
      return undefined
    }
    let live = true
    getExternalSourceCandidates({ source: externalSource, alertId: alert.id, page: 0 }).then(
      (list) => {
        if (!live) return
        setExternalCandidates(list.candidates)
        setExternalMaxResults(list.maxResults)
      },
      () => {
        if (!live) return
        setExternalCandidates([])
        setExternalMaxResults(0)
      },
    )
    return () => {
      live = false
    }
  }, [externalSource, current])

  /**
   * The source's rows in the shape the slim row reads. `id` is a NUMBER there and an external
   * string here, so the index stands in for it — the row uses it for its `cand-` class and for
   * selection, and nothing on this screen acts on an external candidate yet.
   */
  const externalRows: SlimCandidate[] | undefined = externalCandidates?.map((candidate, index) => ({
    id: index,
    name: candidate.name ?? null,
    externalId: candidate.id,
    lastPosition: candidate.lastPosition ?? null,
    lastCompany: candidate.lastCompany ?? null,
    location: candidate.location ?? null,
    cvDate: candidate.cvDate ?? null,
    viewed: candidate.viewed,
    exported: candidate.addedToAts,
  }))

  const atsPage = candidates?.slice((currentPage - 1) * CANDIDATE_PAGE_SIZE, currentPage * CANDIDATE_PAGE_SIZE)
  // ⚠️ THE EXTERNAL LIST IS PAGED BY THE SERVER, NOT SLICED HERE — it takes a `page` and
  // answers one page at a time, which is the opposite of the ATS list's rule above.
  const pageCandidates: SlimCandidate[] | undefined = externalSource === null ? atsPage : externalRows
  // `updateTotalCandidates` (component:1351): the count is capped before the division, and
  // `floor((n + size - 1) / size)` is a ceiling written the long way.
  const pagedTotal = Math.min(
    externalSource === null ? (totalResults ?? candidates?.length ?? 0) : (externalRows?.length ?? 0),
    CANDIDATE_MAX_RESULTS,
  )
  const lastPage = Math.max(1, Math.ceil(pagedTotal / CANDIDATE_PAGE_SIZE))
  // The pager's `ng-if`: `data.maxResults > pageSize` — the UNCAPPED total, so ten or fewer
  // results draw no pager at all (it read "Prev 1/1 Next" over a single row). The external
  // review's template has the same test on `currentData.maxResults`.
  //
  // ⚠️ NOT YET THE WHOLE RULE: upstream also requires `!extensionAlert.hostSite ||
  // hunterSearchManager.showResults`, which hides the pager while a host-page (hunter) search
  // is running. That search arrives with the content scripts (Phase 6.4) and has no state here.
  const showPager = (externalSource === null ? (totalResults ?? candidates?.length ?? 0) : externalMaxResults) > CANDIDATE_PAGE_SIZE


  /**
   * "All in results" — every candidate the FILTER matches, not the twenty on screen.
   *
   * ⚠️ IT NEEDS A SECOND CALL, AND THE SAME PARAMETERS AS THE LIST.
   * `getCandidateDetailsByAlertId` returns ids and names for the whole result set, which is
   * what upstream's `data.allRelevantDetails` holds; sending different filter parameters than
   * the list did would select people the recruiter cannot see.
   *
   * ⚠️ AND IT MERGES RATHER THAN REPLACES. Upstream's loop sets each id to `true` over the
   * existing map, so a row ticked by hand before the menu was opened stays ticked — replacing
   * the set would be identical in the common case and would silently drop a selection in the
   * one where the result set has changed under it.
   */
  const selectAllInResults = async (): Promise<void> => {
    const alert = current?.alert
    if (alert === undefined) return
    try {
      const response = await getCandidateDetailsByAlertId({
        alertId: alert.id,
        sortBy: sortOption.field,
        filter: buildCandidateFilter(shownOption, cvDateOption.value),
      })
      const next = new Set([...selectedIds, ...response.details.map((detail) => detail.id)])
      setSelectedIds(next)
      trackCandidateSelection.multi(alert.id, 'selectAllInResults', next.size)
      // `data.allRelevantDetails` — kept, because the messaging check reads the blacklist reason
      // of a selected id that is not on the current page from here.
      setAllDetails(response.details)
    } catch {
      // As elsewhere on this screen: upstream's error modal has not landed, so the selection
      // simply does not grow rather than the bar claiming a count it does not have.
    }
  }

  /**
   * `OnCandidateHidden` (component:1864) → `removeCandidatesFromListAndRefreshPage` — the ONE
   * place every archive lands, whether it came from a row, the bar or the profile. See
   * `hideFromList.ts`: unless the filter shows archived rows, the rows leave the list, the total
   * and the selection, and an open preview of a single archived row moves to the next row — or,
   * with none, closes.
   */
  const onCandidatesHidden = (alertId: number, ids: readonly number[], hiddenDate: number): void => {
    const idSet = new Set(ids)
    const stay = archivedRowsStay({
      showHidden: shownOption.showHidden,
      showExportedOnly: shownOption.showExportedCandidatesOnly,
      showMessagedOnly: shownOption.showMessagedCandidatesOnly,
    })
    if (stay) {
      setCandidates((previous) =>
        previous === undefined ? previous : applyToIds(previous, idSet, (row) => applyHide(row, hiddenDate)),
      )
      return
    }
    const next = removeFromList(alertId, ids)
    // `uncheckCandidateOnSingleAction` — and a bulk archive resets the selection anyway.
    if (ids.some((id) => selectedIds.has(id))) {
      setSelectedIds(new Set([...selectedIds].filter((id) => !idSet.has(id))))
    }
    if (next !== undefined) {
      openCandidate(next.id, next.algorithm)
      return
    }
    if (ids.length === 1 && openCandidateId === ids[0]) {
      void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(alertId)}`)
    }
  }

  /**
   * The list half of `removeCandidatesFromListAndRefreshPage`: the rows, the total and the tab's
   * count. Answers the row whose preview should open next (`hideFromList.ts`).
   */
  const removeFromList = (alertId: number, ids: readonly number[]): AlertCandidate | undefined => {
    const idSet = new Set(ids)
    const next = nextRowAfterArchive(candidates ?? [], ids, selectedRow.current)
    // `persistState(keepSelectedCandidate ? selectedCandidateId : undefined, …)` — the selection
    // survives only while it is still on this page; the next row, if one opens, persists after.
    listIdsRef.current = listIdsRef.current.filter((id) => !idSet.has(id))
    const pageIds = listIdsRef.current.slice((currentPage - 1) * CANDIDATE_PAGE_SIZE, currentPage * CANDIDATE_PAGE_SIZE)
    const kept = selectedRow.current !== undefined && pageIds.includes(selectedRow.current) ? selectedRow.current : undefined
    selectedRow.current = kept
    persistListState(kept)
    setCandidates((previous) => (previous === undefined ? previous : withoutRows(previous, idSet)))
    setTotalResults((total) => (total === undefined ? total : Math.max(0, total - ids.length)))
    setTabData((previous) => {
      const entry = previous[alertId]
      return entry === undefined
        ? previous
        : { ...previous, [alertId]: { ...entry, totalResults: Math.max(0, entry.totalResults - ids.length) } }
    })
    return next
  }

  /**
   * `exportCandidatesDecisionData` (component:1155) after the plain ATS export or the CSV: the rows
   * become exported and viewed, and leave the list unless it is the Exported (or actioned) view.
   * The ATS export then closes the snippet (`resetCheckboxesAndCloseSnippet`); the CSV only resets
   * the checkboxes, so an open preview advances as it does on an archive.
   */
  const onCandidatesExported = (alertId: number, ids: readonly number[], closeSnippet: boolean): void => {
    const idSet = new Set(ids)
    const stay = shownOption.showExportedCandidatesOnly || shownOption.showActionedAndUnactionedCandidates
    let next: AlertCandidate | undefined
    if (stay) {
      setCandidates((previous) =>
        previous === undefined ? previous : applyToIds(previous, idSet, (row) => ({ ...row, exported: true, viewed: true })),
      )
    } else {
      next = removeFromList(alertId, ids)
    }
    clearCheckedEntities(alertId)
    if (closeSnippet) {
      if (openCandidateId !== undefined) void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(alertId)}`)
      return
    }
    if (next !== undefined) openCandidate(next.id, next.algorithm)
  }

  /** The profile behind this list emits its flag changes; apply them here (component:1864). */
  useShortlistFlagEvents((event) => {
    const alert = current?.alert
    if (event.entity !== 'candidates' || alert === undefined || event.alertId !== alert.id) return
    if (event.kind === 'hide') {
      onCandidatesHidden(alert.id, event.ids, event.hiddenDate)
      return
    }
    const ids = new Set(event.ids)
    setCandidates((previous) =>
      previous?.map((row) => {
        if (!ids.has(row.id)) return row
        if (event.kind === 'favor') return applyFavor(row)
        if (event.kind === 'unfavor') return applyUnfavor(row)
        return applyUnhide(row)
      }),
    )
  })

  /**
   * A profile inside this review asking to message its entity (`OnSingle…MessageClick`): exactly
   * what the row's Message does — tracked here, ADDED to the selection, then the composer at
   * `…-messages?alertId=…`. See `requestShortlistMessage`.
   */
  useShortlistMessageRequests((request) => {
    const alert = current?.alert
    if (request.entity !== 'candidates' || alert === undefined || request.alertId !== alert.id) return
    const row = (candidates ?? []).find((candidate) => candidate.id === request.row.id) ?? (request.row as AlertCandidate)
    onRowAction(row, { name: ProfileAction.Message } as ProfileActionDefinition)
  })

  /**
   * A profile inside this review asking to export its candidate (`OnSingleCandidateExportClick`,
   * cee70a51e8): exactly the row's Export. See `requestShortlistExport`.
   */
  useShortlistExportRequests((request) => {
    const alert = current?.alert
    if (request.entity !== 'candidates' || alert === undefined || request.alertId !== alert.id) return
    const row = (candidates ?? []).find((candidate) => candidate.id === request.row.id) ?? (request.row as AlertCandidate)
    onRowAction(row, { name: ProfileAction.Export } as ProfileActionDefinition)
  })

  /**
   * One candidate's hover action — the row strip's handlers (component:440): Download saves the
   * .docx, Send-details opens the confirmation modal that mails it, Add-to-job and Message ADD the
   * row to the selection and then act on the whole selection, and Favor/Archive toggle the flag
   * and apply it to the row in place (`candidateFlags.ts`).
   */
  const onRowAction = (candidate: AlertCandidate, action: ProfileActionDefinition): void => {
    const alert = current?.alert
    if (alert === undefined) return

    // `downloadWooProfile` — a plain GET saved as a file (the candidate's .docx).
    if (action.name === ProfileAction.Download) {
      trackCandidate.download(candidate, 'list', alert.id)
      void downloadWooProfile(candidate.id).then(saveFile, () => undefined)
      return
    }

    // `assignToJobInAts` (candidate-actions.service:303): the ATS assigns, so the PARENT frame —
    // the content script on the ATS page — is told which candidate. Upstream also persists this
    // row as the selected one (`persistState(candidate.id, true)`), and so does this, after the send.
    if (action.name === ProfileAction.AssignToJob) {
      trackCandidate.assignToJob(candidate, 'list')
      sendAssignToJob(candidate, alert.id)
      selectedRow.current = candidate.id
      persistListState(candidate.id)
      return
    }

    // `sendCandidateDetails` — emails the recruiter the candidate's details; the modal runs it.
    if (action.name === ProfileAction.SendCandidateDetails) {
      trackCandidate.emailDetails(candidate, 'list')
      setSendingDetailsFor(candidate.id)
      return
    }

    if (action.name === ProfileAction.AddToJob) {
      trackCandidate.addToJob(candidate)
      addToSelection(alert.id, candidate)
      void navigate(panelPath('add-to-job', alert))
      return
    }

    if (action.name === ProfileAction.Message) {
      trackCandidate.message(candidate)
      openMessaging(alert, addToSelection(alert.id, candidate))
      return
    }

    // SINGLE_PROFILE_EXPORT's row Export (`onExportCandidate`, cee70a51e8) — the bulk flow for one
    // candidate. It TICKS the row, as single add-to-job does, so the existing decision-data path
    // marks it exported; then the picker over the whole selection, or the plain export of it.
    if (action.name === ProfileAction.Export) {
      trackCandidate.exportClicked(candidate, 'list')
      const ids = addToSelection(alert.id, candidate)
      if (exportToExistingList) {
        void navigate(panelPath('export', alert))
        return
      }
      setExportingToAts(ids)
      return
    }

    if (action.name === ProfileAction.Favor) {
      const undo = candidate.favorite === true
      if (undo) trackCandidate.unfavor(candidate, 'list')
      else trackCandidate.favor(candidate, 'list')
      void Promise.resolve(undo ? unfavorCandidate(alert.id, candidate.id) : favorCandidates(alert.id, [candidate.id]))
        .then(
          () => {
            setCandidates((previous) =>
              previous?.map((row) =>
                row.id === candidate.id ? (undo ? applyUnfavor(row) : applyFavor(row)) : row,
              ),
            )
          },
          () => undefined,
        )
      return
    }

    if (action.name === ProfileAction.Archive) {
      if (candidate.hidden === true) trackCandidate.unhide(candidate, 'list')
      else trackCandidate.hide(candidate, 'list')
      if (candidate.hidden === true) {
        void unhideCandidate(alert.id, candidate.id).then(
          () => {
            setCandidates((previous) =>
              previous?.map((row) => (row.id === candidate.id ? applyUnhide(row) : row)),
            )
          },
          () => undefined,
        )
        return
      }
      void hideCandidates(alert.id, [candidate.id]).then(
        (hiddenDate) => {
          onCandidatesHidden(alert.id, [candidate.id], hiddenDate)
        },
        () => undefined,
      )
    }
  }

  /**
   * ⚠️ THE THREE OUTWARD ACTIONS NAVIGATE; THEY DO NOT WRITE. Upstream's handlers for
   * add-to-job, export and message are `$state.go(...)` plus a re-seed of the checkbox
   * container (`addToJobInAts:1096`, `goToExportCandidatesState:1072`, `messageCandidates:923`)
   * — the panel that opens is what eventually posts, and its submit is the button this port
   * deliberately leaves inert. Leaving the BAR inert as well was a different thing: measured
   * beside build 354, prod opened `…/review/add-to-job` from the same click and this build did
   * nothing at all, so three of the five bulk actions were unreachable rather than withheld.
   *
   * The selection travels as outlet context, which is how `AddToJobRoute` and the composer
   * already read it — `initCandidateCheckboxContainer` is upstream's equivalent and exists
   * because its container is a service rather than a parent component.
   */
  /**
   * The ☰ menu's three remaining items. "Search criteria" fetches the beautified criteria and
   * shows them in a tooltip over the header; "Delete" asks first.
   */
  const [criteriaData, setCriteriaData] = useState<AlertCriteriaData | undefined>(undefined)
  const [criteriaOpen, setCriteriaOpen] = useState(false)
  const [deleting, setDeleting] = useState<ExtensionAlert | undefined>(undefined)

  /**
   * `loadExtensionTabData` — the per-alert counts the panel's rows show. Fetched when the
   * panel opens rather than on mount: it is one call per alert, and most sessions never open
   * it.
   */
  const [tabData, setTabData] = useState<Record<number, { totalResults: number; totalNewTopMatchesResults: number; totalNewTopMatchesSinceLastViewedAlerts: number }>>({})
  const loadTabData = (): void => {
    for (const container of containers ?? []) {
      const id = container.alert.id
      if (tabData[id] !== undefined) continue
      void getCandidateNumbersByAlertId(id).then(
        (numbers) => {
          setTabData((previous) => ({ ...previous, [id]: numbers }))
        },
        () => undefined,
      )
    }
  }

  const openSearchCriteria = (id: number): void => {
    // Upstream clears the panel and re-fetches on every open; the service caches by alert id,
    // which is the same thing one navigation later.
    setCriteriaData(undefined)
    setCriteriaOpen(true)
    void getBeautifiedCriteria(id).then(
      (loaded) => {
        setCriteriaData(loaded)
      },
      () => {
        setCriteriaOpen(false)
      },
    )
  }

  /**
   * `enableAutomation(alert.id)` — only ever reached from the confirmation's Activate button.
   *
   * ⚠️ WHAT THIS DOES IS MAIL PEOPLE. The dialogue above it quotes the count for that reason.
   * `automationMode` is updated locally the way upstream does (`extensionAlert.automationMode =
   * Enabled`) so the banner becomes the switch without a refetch.
   */
  const onConfirmAutomation = (): void => {
    const alert = current?.alert
    setActivatingAutomation(false)
    if (alert === undefined) return
    void enableAutomation(alert.id).then(
      () => {
        setContainers((previous) =>
          previous?.map((container) =>
            container.alert.id === alert.id
              ? { ...container, alert: { ...container.alert, automationMode: HunterAutomationMode.Enabled } }
              : container,
          ),
        )
      },
      () => undefined,
    )
  }

  /** `onHunterAutomationSwitchToggle` — flips between Enabled and Paused, no confirmation. */
  const onToggleAutomation = (): void => {
    const alert = current?.alert
    if (alert === undefined) return
    const next =
      alert.automationMode === HunterAutomationMode.Enabled
        ? HunterAutomationMode.Paused
        : HunterAutomationMode.Enabled
    void setAutomationMode(alert.id, next).then(
      () => {
        setContainers((previous) =>
          previous?.map((container) =>
            container.alert.id === alert.id
              ? { ...container, alert: { ...container.alert, automationMode: next } }
              : container,
          ),
        )
      },
      () => undefined,
    )
  }

  const confirmDelete = (): void => {
    const alert = deleting
    setDeleting(undefined)
    if (alert === undefined) return
    void deleteAlertById(AlertEntity.Candidates, alert.id).then(
      () => {
        // `onDeleteAlert`: drop it, then go to the first alert left — or to the review with
        // none, which is what `targetAlertId = null` means.
        setContainers((previous) => previous?.filter((container) => container.alert.id !== alert.id))
        const next = containers?.find((container) => container.alert.id !== alert.id)?.alert.id
        void navigate(
          next === undefined
            ? '/chrome-extension/extension/shortlist/review'
            : `/chrome-extension/extension/shortlist/review?alertId=${String(next)}`,
        )
      },
      () => undefined,
    )
  }

  const panelPath = (leaf: string, alert: { id: number }, extra?: Record<string, string>): string => {
    const query = new URLSearchParams({ alertId: String(alert.id), ...extra })
    return `/chrome-extension/extension/shortlist/review/${leaf}?${query.toString()}`
  }

  /**
   * `attemptMessageCandidates` (component:897), shared by the bar and the rows.
   *
   * ⚠️ THE DESTINATION IS DECIDED BY A FETCH, NOT BY A FLAG. It reads the mailbox connection
   * first and sends an unconnected user to `email-signin` instead; `messageCandidatesEnabled`
   * only says the tenant HAS messaging. Before that, a selection in which NOBODY can be mailed
   * (no email, or a blacklisted one) stops with upstream's dialog. An EMPTY selection is allowed:
   * the composer opens with no recipients, as upstream's does.
   */
  const openMessaging = (alert: { id: number }, ids: readonly number[]): void => {
    if (!hasValidIdsForMessagingOrEmpty(ids, candidates ?? [], allDetails)) {
      setNoValidCandidatesOpen(true)
      return
    }
    void getCandidateEmailIntegrationDetails().then(
      (details) => {
        if (details.enabled) {
          void navigate(panelPath('candidate-messages', alert))
          return
        }
        void navigate(
          panelPath(
            'email-signin',
            alert,
            details.emailServerType === null ? undefined : { emailServerType: details.emailServerType },
          ),
        )
      },
      () => undefined,
    )
  }

  /**
   * `checkedCandidateIdsMap[candidate.id] = true; onCandidateChecked(candidate)` — a row's message
   * and add-to-job ADD that row to the selection, then act on the whole selection, exactly as the
   * bar does (`messageCandidate:868`, `onAddToJobCandidate:1081`). They do not replace it.
   */
  const addToSelection = (alertId: number, candidate: AlertCandidate): number[] => {
    const next = new Set(selectedIds)
    next.add(candidate.id)
    setSelectedIds(next)
    trackCandidateSelection.checked(alertId, candidate, true)
    return [...next]
  }

  /**
   * `onSelectAllInPageClicked` (component:2098) — the page is ADDED to the selection
   * (`checkedCandidateIdsMap[id] = true` over the existing map), not substituted for it.
   */
  const selectAllInPage = (alertId: number): void => {
    const next = new Set([...selectedIds, ...(pageCandidates ?? []).map((candidate) => candidate.id)])
    setSelectedIds(next)
    trackCandidateSelection.multi(alertId, 'selectAllInPage', next.size)
  }

  /**
   * The bar's actions (`getBulkActions` handlers, component:463).
   *
   * ⚠️ AN EMPTY SELECTION IS NOT A NO-OP, AND IT IS NOT THE SAME FOR EVERY ACTION. Add-to-job,
   * Message and the existing-list Export proceed with nothing ticked — their panels have their
   * own pickers. Favor, Archive and the plain Export go through `verifyHasSelectedCandidates`
   * first: with nothing ticked it opens the "Select candidates" modal, and the action runs
   * against whatever the modal left selected — still nothing, if it was dismissed, in which case
   * the handler's own empty check stops it.
   */
  const onBulkAction = (action: BulkAction): void => {
    const alert = current?.alert
    if (alert === undefined) return

    if (action.name === ProfileAction.AddToJob) {
      trackCandidateBulk.addToJob(alert.id, [...selectedIds], candidates ?? [])
      void navigate(panelPath('add-to-job', alert))
      return
    }

    if (action.name === ProfileAction.Message) {
      trackCandidateBulk.message(alert.id, [...selectedIds], candidates ?? [])
      openMessaging(alert, [...selectedIds])
      return
    }

    if (action.name === ProfileAction.Export && exportToExistingList) {
      trackCandidateBulk.exportPanel(alert.id, [...selectedIds], candidates ?? [])
      void navigate(panelPath('export', alert))
      return
    }

    if (selectedIds.size === 0) {
      setMultiSelectAction(action)
      return
    }
    runSelectedAction(alert, action)
  }

  /** The gated half of `onBulkAction`; reads the selection from the store, not the render. */
  const runSelectedAction = (alert: { id: number }, action: BulkAction): void => {
    const ids = getCheckedEntities(alert.id).map((entry) => entry.id)
    if (ids.length === 0) return
    const idSet = new Set(ids)

    // `onExportCandidates:990` — the async ATS export dialogue; it writes, polls and reports.
    if (action.name === ProfileAction.Export) {
      setExportingToAts(ids)
      return
    }

    // `onExportCandidatesToCsvClicked:2305`.
    if (action.name === ProfileAction.ExportToCsv) {
      trackCandidateBulk.exportToCsv(alert.id, ids, candidates ?? [])
      setExportingToCsv(ids)
      return
    }

    if (action.name === ProfileAction.Favor) {
      trackCandidateBulk.favor(alert.id, ids, candidates ?? [])
      void favorCandidates(alert.id, ids).then(
        () => {
          setCandidates((previous) =>
            previous === undefined ? previous : applyToIds(previous, idSet, applyFavor),
          )
          setSelectedIds(new Set())
        },
        () => undefined,
      )
      return
    }

    if (action.name === ProfileAction.Archive) {
      trackCandidateBulk.hide(alert.id, ids, candidates ?? [])
      void hideCandidates(alert.id, ids).then(
        (hiddenDate) => {
          onCandidatesHidden(alert.id, ids, hiddenDate)
          setSelectedIds(new Set())
        },
        () => undefined,
      )
    }
  }

  // `syncFiltersAndSorting()` — run once per alert, before the first fetch, and NOT on every
  // render: it is a seed, so a re-run after the user changes the sort would put the alert's
  // old value straight back.
  //
  // ⚠️ AND IT IS `reloadStateIfNeeded` FIRST (component:526): when this alert's last search is
  // still in storage (see `lastAlertViewed.ts`), the bar is seeded from THAT query
  // (`resetGetAlertDataParamsToState`) rather than from the alert, and the list replays it. A
  // fresh browser profile has nothing stored, which is every baseline capture.
  const syncedAlertId = useRef<number | undefined>(undefined)
  useEffect(() => {
    const alert = current?.alert
    if (alert === undefined || sessionUserId === undefined || syncedAlertId.current === alert.id) return
    syncedAlertId.current = alert.id
    void getUnexpiredLastAlertViewed(ExtensionProduct.Candidates)
      .catch(() => null)
      .then((stored) => {
        if (syncedAlertId.current !== alert.id) return
        // `moveToCandidateId` is a non-URL param of the review state that nothing in this port
        // sets yet (notifications, host-page changes), so it never vetoes a restore here.
        const restore = isRestorable(stored, { userId: sessionUserId ?? undefined, alertId: alert.id }) ? stored : null
        if (restore !== null) {
          const fields = candidateAlertFieldsFromParams(restore.getDataParams)
          const sortBy = restoredSortBy(ExtensionProduct.Candidates, restore.getDataParams) as SortOption['field']
          setSortOption(sortOptionForAlert(sortBy))
          setShownOption(shownOptionForAlert(fields))
          setCvDateOption(cvDateOptionForAlert(fields.filterLastRelevancyXDays))
          // `waitingToSelectedCandidateId` → `selectedCandidateId`: the row is "soft selected"
          // without opening it, so an archive from it advances as from an opened one.
          selectedRow.current = restore.selectedEntityId
        } else {
          setSortOption(sortOptionForAlert(alert.sortBy))
          setShownOption(shownOptionForAlert(alert))
          setCvDateOption(cvDateOptionForAlert(alert.filterLastRelevancyXDays))
        }
        setSortChosen(false)
        setFiltersChosen(false)
        setSeed({ alertId: alert.id, restore })
      })
    // ⚠️ THE SELECTION USED TO BE CLEARED HERE AND MUST NOT BE. It is per alert — upstream
    // rebuilds the checkbox container on the new alert's resolve, and keeping ids across
    // alerts would offer a bulk action over people who are not in the list — but this effect
    // also runs on every REMOUNT, and opening a side-view panel remounts this screen. Clearing
    // here is therefore indistinguishable from "the user pressed a bulk action", which is
    // precisely how the selection was being lost. `entityCheckboxContainer` keys by alert id,
    // so the per-alert guarantee is structural now and needs no reset.
  }, [current, sessionUserId])

  // ⚠️ `rendered` IS "A CHILD IS ACTIVE", NOT "THE SIDE VIEW EXISTS". Upstream's condition is
  // `$state.includes(hunterReview) && !$state.is(hunterReview)` — inside the subtree but not at
  // its root — and without the class the div is `flex: 0`, a zero-width box that takes no space.
  // `useOutlet()` is null for exactly the same set of routes, so the two agree by construction
  // rather than by a second list of child paths that could drift.
  //
  // ⚠️ AND IT CARRIES THE ALERT'S NAME, which is a resolve rather than a convenience. The
  // profile in the side view prints "For <name> hunter" in its matching-summary card, and
  // upstream reads that off `hunterContainer` — resolved HERE, at the review state, and
  // injected into the child's controller. A second `getAlerts` call in the child would be a
  // request per row click for data its parent is already holding.
  /**
   * ⚠️ AND IT CARRIES THE SELECTION, WHICH IT DID NOT, and that was half of a broken write.
   * `AddToJobRoute` reads `checked` off this context and falls back to `[]`, so the panel
   * opened naming nobody and `addToAtsJobAsync` posted an EMPTY `candidateIds`. The server
   * accepts that — a well-formed request for zero candidates — so the failure was a silent
   * no-op against a real ATS rather than an error. The other half was that the selection did
   * not survive the click at all; see `entityCheckboxContainer.ts`.
   *
   * The panel could read the container directly, as upstream's does. It is handed down instead
   * so the panels keep one source of truth whichever screen opens them — the profile's
   * add-to-job seeds a single candidate the same way and has no alert selection behind it.
   */
  /**
   * What the export panel calls once the ATS has confirmed. Upstream does this work in the
   * list component because the panel only emits an event
   * (`exportCandidatesDecisionData:1155`): mark the rows, then empty the selection.
   */
  const onExported = (exportedIds: readonly number[]): void => {
    const ids = new Set(exportedIds)
    if (selectedAlertId !== undefined) trackCandidateBulk.exported(selectedAlertId, exportedIds, candidates ?? [])
    setCandidates((previous) =>
      previous === undefined ? previous : applyToIds(previous, ids, (candidate) => ({ ...candidate, exported: true, viewed: true })),
    )
    clearCheckedEntities(selectedAlertId)
  }

  const sideView = useOutlet(
    current === undefined ? undefined : { alertName: current.alert.name, alertId: current.alert.id, checked, onExported },
  )

  useEffect(() => {
    let live = true
    setFailed(false)
    getAlerts(entity).then(
      (loaded) => {
        if (live) setContainers(loaded)
      },
      () => {
        // ⚠️ IT DOES NOT RETHROW, and an earlier version did. Throwing from a promise
        // rejection handler does not reach a React error boundary — it produces an unhandled
        // rejection, logged by the browser and invisible to the app. The comment beside it
        // also claimed `ApiError` was narrowed "so a 403 is not mistaken for an empty list",
        // which nothing acted on: both paths set `failed`, so the distinction existed only in
        // the comment. And a network failure is usually a `TypeError` from `fetch` rather than
        // an `ApiError` at all, so the narrowing would have rethrown the commonest case.
        //
        // Upstream raises its network-error modal here through the http interceptor. That
        // modal is a shared component that has not landed, so until it does the screen simply
        // does not claim to have loaded.
        if (!live) return
        setFailed(true)
      },
    )
    return () => {
      live = false
    }
  }, [entity])

  // The bootstrap carries the flag. It is fetched here rather than threaded from the root
  // loader because this is the first screen that needs it; when a second one does, it moves.
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setSessionUserId(sessionUserIdOf(model.session))
        setAutomatedHunters(model.featureFlags?.[AUTOMATED_HUNTERS] === true)
        setAutomationEnabled(
          model.featureFlags?.[HUNTER_AUTOMATION] === true && model.hunterAutomationEnabled === true,
        )
        // `window.careero.atsName` — the displayed name of the tenant's own ATS, and the key
        // its logo is looked up by.
        setAtsName(model.atsName ?? undefined)
        setAtsLabel(getAtsLabel(model))
        const messaging = model.featureFlags?.[SHORTLIST_MESSAGE_CANDIDATES] === true
        setMessagingEnabled(messaging)
        const exportFlag = model.featureFlags?.[EXPORT_CANDIDATES] === true
        setExportToExistingList(exportFlag && model.exportCandidatesToExistingListEnabled === true)
        setActionFlags({
          addToAtsJobEnabled: model.featureFlags?.[ADD_TO_JOB] === true && model.addToJobEnabled === true,
          exportCandidatesEnabled:
            exportFlag &&
            (model.exportCandidatesEnabled === true || model.exportCandidatesToExistingListEnabled === true),
          messageCandidatesEnabled: messaging,
          // Negated, and on by default — the flag DISABLES it.
          exportToFileEnabled: model.featureFlags?.[EXPORT_TO_FILE_DISABLED] !== true,
          atsLabel: getAtsLabel(model),
          // ⚠️ THE BULK BAR NAMES THE ATS TOO, and these were the two labels missing from it.
          // `bulkActions` falls back to "Add to job" / "Export" without them, which is the
          // copy a non-Bullhorn tenant gets — so on Bullhorn the bar disagreed with the row
          // strip beside it, which had the names.
          addToJobTitle: addToJobTitle(model.atsName ?? undefined),
          exportTitle: exportTitle(model.atsName ?? undefined),
        })
        // ⚠️ `showAlgorithm` IS AN ENVIRONMENT FLAG, NOT A FEATURE FLAG
        // (extension-alert-candidates.component.ng1.ts:439). It puts the raw match score in
        // brackets after every name — "SEUN ABODERIN (160)" — and it is ON for dev and test
        // tenants and OFF in production. Which matters here specifically: every reference
        // capture is taken against a dev tenant, so a port that hardcodes it either way is
        // wrong against one of the two, and hardcoding it OFF reads as a diff on every row.
        //
        // ⚠️ AND IT ARRIVED WITHOUT A CHANGE HERE, WHICH IS THE POINT. `ENV` is a plan §4.1
        // Track-B field: the JSP inlines it into `window.careero` server-side, so upstream
        // always had it and the REST DTO did not. This line read `undefined` and resolved to
        // `false` for as long as that was true, and the score suffix was missing from every row
        // against the reference — 645 pixels on this screen alone. The careero PR landed on the
        // dev tenant on 16 Sep 2026 and the same line now resolves to `true`, taking the screen
        // to 46 pixels. Reading the flag from somewhere else — the hostname, an env var — would
        // have hidden a Track-B dependency behind a rule this port invented, and would have
        // needed unpicking now.
        setShowAlgorithm(model.ENV?.IS_DEV === true || model.ENV?.IS_TEST === true)
        // ⚠️ KEPT OUT OF `actionFlags`, WHICH IS THE BULK-ACTION CONTRACT. This flag decides
        // one entry in the FILTER menu; folding it in would make `bulkActions` take a
        // parameter it never reads and couple two lists that only share a fetch.
        setActionedViewEnabled(model.featureFlags?.[SHORTLIST_ACTIONED_AND_UNACTIONED_VIEW] === true)
        // `viaHunter: true` — this IS the hunter review, which is what puts Favour and
        // Archive in the strip at all (`profileActions`).
        setRowActions(
          profileActions({
            downloadWooProfileEnabled: model.featureFlags?.[EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED] === true,
            viaHunter: true,
            assignToJobEnabled: model.featureFlags?.[ASSIGN_TO_JOB] === true && model.assignToJobEnabled === true,
            addToAtsJobEnabled: model.featureFlags?.[ADD_TO_JOB] === true && model.addToJobEnabled === true,
            messageCandidatesEnabled: messaging,
            sendCandidateDetailsEnabled: model.featureFlags?.[SEND_CANDIDATE_DETAILS_TO_USER] === true,
            reportEnrichmentEnabled: true,
            // The list wires `exportToAtsList` (cee70a51e8); the flag decides.
            singleProfileExportEnabled: singleProfileExportEnabled(model),
            exportTitle: exportTitle(model.atsName ?? undefined),
            atsLabel: getAtsLabel(model),
            addToJobTitle: addToJobTitle(model.atsName ?? undefined),
          }),
        )
      },
      () => {
        // A bootstrap that cannot be read is not evidence of either value, and rendering the
        // wrong panel is worse than rendering none — so the panel stays closed until it is
        // known. `automatedHunters` therefore stays undefined rather than becoming false.
        // The restore must not wait on it forever, though: with no user to compare, a state
        // stamped with one is not restored and the list starts a new search.
        if (live) setSessionUserId(null)
      },
    )
    return () => {
      live = false
    }
  }, [])

  // ⚠️ THE JOBS ARE SEARCHED ON INIT, NOT ON THE FIRST KEYSTROKE. Upstream's `$onInit` calls
  // `searchExtensionJobs()` with no query whenever automation is on (component:144), so the
  // Jobs group has its rows and its count the moment the panel opens. Waiting for input would
  // show "(0/0)" on a tenant that has jobs, which reads as "none match" rather than "not asked
  // yet".
  //
  // It hangs off the component rather than the panel's open state for the same reason: the
  // dropdown component is instantiated with the screen, not when the panel is opened.
  useEffect(() => {
    if (automatedHunters !== true || containers === undefined) return undefined
    const bound = (containers.find((container) => container.alert.id === alertId) ?? containers[0])?.alert
      .extensionJobId
    let live = true
    setSearching(true)
    searchExtensionJobs({ exclude: typeof bound === 'number' ? [bound] : [] })
      .then(
        (response) => {
          if (live) setJobs(response)
        },
        () => {
          // See `runJobSearch` — the modal that reports this upstream has not landed.
        },
      )
      .finally(() => {
        if (live) setSearching(false)
      })
    return () => {
      live = false
    }
  }, [automatedHunters, containers, alertId])

  // The candidates for the alert on screen. Refetched when the alert changes AND when the
  // sort does — `changeSortOrder` calls `startNewSearch()`, which is this effect.
  //
  // ⚠️ THE FILTER IS SENT ON EVERY FETCH, NOT ONLY WHEN IT CHANGES, and it has to be: the
  // server merges what it is given onto the filter it persisted for the alert, so omitting
  // the filter on a sort change would leave the previous one in force while the bar shows
  // something else. `buildCandidateFilter` sends all six flags for the same reason.
  //
  // ⚠️ `persistFilterAndSort` IS A WRITE DRESSED AS A READ, and it is upstream's behaviour on
  // the normal list fetch — it is what makes the next session open the alert the way this one
  // left it. It is set only once the bar is in a state the USER chose: seeding the bar from
  // the alert and then persisting it back would be a write for every screen that merely
  // rendered, including every reference capture.
  //
  // ⚠️ A RESTORE IS THE OTHER BRANCH (`reloadPreviousSearchFromState`, component:1311): the stored
  // ids as `limitCandidateIds`, no `persistFilterAndSort`, the stored page, and NO
  // `HunterResultsLoaded` and no new stored state — measured against upstream, a reload of the
  // review or of a review-profile URL records nothing but its page view. A sort or filter the
  // user picks afterwards is a new search again.
  useEffect(() => {
    const id = containers?.find((container) => container.alert.id === alertId)?.alert.id ?? containers?.[0]?.alert.id
    if (id === undefined || seed?.alertId !== id) return undefined
    let live = true
    const restore = sortChosen || filtersChosen ? null : seed.restore
    const sortBy = sortOption.field
    const filter = buildCandidateFilter(shownOption, cvDateOption.value)
    getCandidatesByAlertId({
      alertId: id,
      sortBy,
      filter,
      ...(restore !== null ? { limitCandidateIds: restore.allEntityIds } : {}),
      ...(sortChosen || filtersChosen ? { persistFilterAndSort: true } : {}),
    }).then(
      (list) => {
        if (!live) return
        setCandidates(list.candidatePage)
        setTotalResults(list.maxResults)
        if (restore !== null) {
          setCurrentPage(restoredPage(restore.selectedPage, list.candidatePage.length, CANDIDATE_PAGE_SIZE))
          // `scrollToCandidate` after the 250ms refresh and its own 500ms.
          const selected = restore.selectedEntityId
          if (selected !== undefined) window.setTimeout(() => scrollToRestoredRow(selected), 750)
          return
        }
        // `startNewSearch()` resets to the first page: a new alert, sort or filter makes the
        // old page number meaningless, and page 30 of a result set that now has 8 rows shows
        // nothing at all.
        setCurrentPage(1)
        // `persistState(candidateId, …)` at the end of `startNewSearch`. Upstream passes no
        // row, and its `navigateToCandidate` then CLOSES an open preview (measured: a cold
        // `…/review/candidate/profile` URL with nothing stored lands on `/review`). This screen
        // keeps the preview, so the row it shows is the selection it records.
        selectedRow.current = openCandidateRef.current
        void persistLastAlertViewed(ExtensionProduct.Candidates, {
          ...(typeof sessionUserId === 'number' ? { userId: sessionUserId } : {}),
          selectedAlertId: id,
          ...(openCandidateRef.current === undefined ? {} : { selectedEntityId: openCandidateRef.current }),
          allEntityIds: list.candidatePage.map((candidate) => candidate.id),
          selectedPage: 1,
          getDataParams: { alertId: id, sortBy, filter: { ...filter } },
        })
      },
      () => {
        // As elsewhere on this screen: upstream's modal has not landed, so the list simply
        // does not appear rather than the screen claiming something it cannot show.
      },
    )
    return () => {
      live = false
    }
  }, [containers, alertId, seed, sessionUserId, sortOption, shownOption, cvDateOption, sortChosen, filtersChosen])

  // `HunterResultsLoaded` for every search the list makes — see `recordCandidateSearch`. Not
  // for a restore, which upstream does not record.
  useEffect(() => {
    if (externalSource !== null) return
    const id = containers?.find((container) => container.alert.id === alertId)?.alert.id ?? containers?.[0]?.alert.id
    if (id === undefined || seed?.alertId !== id) return
    if (seed.restore !== null && !sortChosen && !filtersChosen) return
    recordCandidateSearch(id, sortOption.field, buildCandidateFilter(shownOption, cvDateOption.value))
  }, [containers, alertId, seed, sortOption, shownOption, cvDateOption, sortChosen, filtersChosen, externalSource])

  useEffect(() => {
    let live = true
    getExternalSourceConfiguration().then(
      (response) => {
        if (live) setSources(response)
      },
      () => {
        // A tenant with no external sources 404s or returns nothing here; either way the
        // selector does not render, which is the same outcome as an empty list.
      },
    )
    return () => {
      live = false
    }
  }, [])

  // ⚠️ THE WHOLE ROW WAITS ON THE LIST, which is what upstream does: every branch of
  // `shortlist-extension-alerts.tpl.html` is behind `huntersContainerLoaded`, so there is no
  // state in which it renders a name it does not have. Rendering an empty row first and
  // filling it in would be a flicker this port invented.
  // `initUserDepartments` (component:516). Upstream asks on every init; only the empty state
  // reads the answer, so it is asked for when that state is what will render.
  const empty = containers !== undefined && containers.length === 0
  useEffect(() => {
    if (!empty) return undefined
    let live = true
    getUserDepartments().then(
      (loaded) => {
        if (live) setDepartments(sortNoneOfTheAboveLast(loaded))
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [empty])

  if (containers === undefined || failed) return <div className="shortlist-hunter-review" />

  const renderedAt = Date.now()

  /**
   * `searchExtensionJobs` (component:350) — the Jobs group's rows.
   *
   * ⚠️ THE EXCLUDE LIST IS THE CURRENT ALERT'S JOB, AND IT IS AN EMPTY ARRAY OTHERWISE, never
   * absent: upstream builds `[]` when the alert has no `extensionJobId`, and `$http` still
   * sends `exclude[]=`. The observed request from the live app is `search?exclude%5B%5D=`,
   * which is what this reproduces.
   */
  const runJobSearch = async (query: string): Promise<void> => {
    const bound = current?.alert.extensionJobId
    // ⚠️ SEQUENCED, because a search box fires one request per keystroke and they do not come
    // back in order. A slow short query ("ja") landing after a fast long one ("java") would
    // otherwise overwrite the newer results — and its `finally` would clear `searching` while
    // the newer request is still in flight, turning the spinner off mid-search. Only the
    // latest request may write.
    const seq = (jobSearchSeq.current += 1)
    setSearching(true)
    try {
      const response = await searchExtensionJobs({
        query,
        exclude: typeof bound === 'number' ? [bound] : [],
      })
      if (seq === jobSearchSeq.current) setJobs(response)
    } catch {
      // Same reasoning as the alerts call: the modal that reports this upstream is a shared
      // component that has not landed, so the group simply shows what it last had.
    } finally {
      if (seq === jobSearchSeq.current) setSearching(false)
    }
  }

  /**
   * `selectExtensionJobFromSlimHeaderDropdown` / `onExtensionJobSelected` → `redirect-job-to-alert`
   * (`redirect-job-to-alert.resolver.ng1.ts:43`). Upstream navigates with `extensionJobId` and lets
   * a RESOLVER turn it into an `alertId`; there is no resolver layer here, so the same two steps
   * happen in the handler.
   *
   * ⚠️ LOOK BEFORE CONVERTING. The resolver searches the alerts already loaded for one whose
   * `extensionJobId` matches and only calls `convertJobToAlert` when there is none — because that
   * call CREATES an alert. Converting on every click would make a duplicate alert each time a job
   * row is opened.
   */
  const openJob = (job: { id: number }): void => {
    const existing = containers.find((container) => container.alert.extensionJobId === job.id)?.alert.id
    if (existing !== undefined) {
      void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(existing)}`)
      return
    }
    void convertJobToAlert(job.id).then(
      (created) => {
        // `addExtensionAlertContainer(alertContainer, true)` — the new alert joins the list before
        // the navigation, so the review finds it.
        setContainers((previous) => [...(previous ?? []), created])
        void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(created.alert.id)}`)
      },
      () => undefined,
    )
  }

  if (current === undefined) {
    // No alerts at all: upstream's slim "no alerts" branch (`shortlist-extension-alerts.tpl.html`),
    // with the side view beside it rendering nothing, as it does with no alert selected.
    //
    // ⚠️ NOT PORTED: `CreateFirstAlertJobClicked`, the action-tracking event upstream sends when a
    // job is picked here. The SPA has no tracking client yet (see `useCloseDialog.ts`), and
    // analytics strings are a contract, so it arrives with that client rather than as a guess.
    return (
      <div className="shortlist-hunter-review">
        <ExtensionAlertsHost entity="candidates" flexBasis={ExtensionViewWidth.Slim}>
          <NoAlertsSlim
            {...(departments === undefined ? {} : { departments })}
            automatedHuntersEnabled={automatedHunters === true}
            jobs={jobs?.results ?? []}
            onSearchJobs={async (query) => (await searchExtensionJobs({ query, exclude: [] })).results}
            onSelectJob={openJob}
            // `createNewAlert()` — `HunterEditAlert` with `NEW_ALERT_ID`.
            onCreateAlert={() => {
              void navigate(`/chrome-extension/extension/shortlist/edit-alert?alertId=${String(NEW_ALERT_ID)}`)
            }}
            onSelectDepartment={async (departmentId) => {
              await updateUserDepartments([departmentId])
            }}
          />
        </ExtensionAlertsHost>
      </div>
    )
  }

  return (
    <div className="shortlist-hunter-review">
      <ExtensionAlertsHost entity="candidates" flexBasis={ExtensionViewWidth.Slim}>
        <div className="shortlist-content-container shortlist-content-container-slim">
          <SlimHeaderDropdown
            alert={current.alert}
            // `editExtensionAlert` — `$state.go(HunterEditAlert, { alertId: alert.id })`. The
            // pen rendered with `role="button"` and an `onClick` bound to an undefined prop,
            // so React attached no handler at all: drawn, hoverable, inert.
            onEdit={() => {
              void navigate(
                `/chrome-extension/extension/shortlist/edit-alert?alertId=${String(current.alert.id)}`,
              )
            }}
            // ⚠️ THE NAME, NOT THE BARS. The alert list hangs off
            // `.shortlist-header-slim-alert-name` upstream — the element carrying
            // `dropdown-enabled` and its ▾ — and the ☰ beside it opens a different, four-item
            // menu. This call had them crossed, so the ▾ did nothing and the ☰ opened the list.
            onToggleAlertList={() => {
              setPanelOpen((open) => {
                if (!open) loadTabData()
                return !open
              })
            }}
            alertListOpen={panelOpen}
            // The wrap's layout class — see `SlimHeaderDropdown`.
            automatedHunters={automatedHunters === true}
            onToggleActions={() => {
              setSlimActionsOpen((open) => !open)
            }}
            actionsOpen={slimActionsOpen}
            // `openAtsFilters()` — the edit screen opened straight onto its ATS-filters tab.
            // Gated on the alert HAVING filters, as upstream gates the item.
            // ⚠️ FALSE IN PRODUCTION. `isCriteriaAtsFiltersEnabled` is the literal `true`
            // upstream with its feature flag commented out, so `isHasAtsFilters &&
            // !isCriteriaAtsFiltersEnabled` never holds and the item is never drawn. This
            // screen was passing the ALERT's shape instead, which put a "Filters" entry in a
            // menu production fills with Search criteria / Duplicate / Delete.
            showAtsFilters={false}
            onAtsFilters={() => {
              setSlimActionsOpen(false)
              void navigate(
                `/chrome-extension/extension/shortlist/edit-alert?alertId=${String(current.alert.id)}&atsFilters=true`,
              )
            }}
            // `isBeautifiedSearchCriteriaEnabled` — `!$stateParams.externalSource`.
            showSearchCriteria={routeParams.get('externalSource') === null}
            onSearchCriteria={() => {
              setSlimActionsOpen(false)
              openSearchCriteria(current.alert.id)
            }}
            // `duplicateAlertFromSlimHeader()` — the edit screen on a NEW alert that carries
            // `duplicate` and the id it is copied FROM. Not a write by itself.
            onDuplicate={() => {
              setSlimActionsOpen(false)
              void navigate(
                `/chrome-extension/extension/shortlist/edit-alert?alertId=${String(NEW_ALERT_ID)}` +
                  `&duplicate=true&originAlertId=${String(current.alert.id)}`,
              )
            }}
            // ⚠️ THE CONFIRMATION IS NOT OPTIONAL. `onDeleteAlert` asks `showDeleteDialogue`
            // first and returns on a no; the endpoint destroys a recruiter's saved search.
            onDelete={() => {
              setSlimActionsOpen(false)
              setDeleting(current.alert)
            }}
            // Inside the trigger, not beside it — see `SlimHeaderDropdown.panel`.
            {...(panelOpen && automatedHunters !== undefined
              ? {
                  panel: (
            <SlimHeaderDropdownPanel
                alerts={containers.map((container) => container.alert)}
                currentAlertId={current.alert.id}
                // ⚠️ `extensionAlertsTabsData[alert.id]` — WITHOUT IT EVERY ROW IN THE PANEL IS
                // JUST A NAME. `ExtensionAlertTab` draws the candidate count and the new-match
                // badge from this, and the panel passed none: beside build 354 prod's row read
                // "Test · 0 · 438 candidates" and this one read "Test".
                tabData={tabData}
                currentExtensionJobId={current.alert.extensionJobId}
                automatedHunters={automatedHunters}
                automationEnabled={automationEnabled}
                jobs={jobs}
                searching={searching}
                onSearch={(query) => {
                  void runJobSearch(query)
                }}
                onSelectAlert={(alert) => {
                  setPanelOpen(false)
                  void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(alert.id)}`)
                }}
                // `selectNewAlertFromDropdown()` — the edit screen with the NEW sentinel, and
                // `originAlertId` so it knows where the user came from. Declared and never
                // passed, which is why "Add search" drew correctly and did nothing at all.
                onAddSearch={() => {
                  setPanelOpen(false)
                  void navigate(
                    `/chrome-extension/extension/shortlist/edit-alert?alertId=${String(NEW_ALERT_ID)}` +
                      `&originAlertId=${String(current.alert.id)}`,
                  )
                }}
                /**
                 * `selectExtensionJobFromSlimHeaderDropdown` → `redirect-job-to-alert`
                 * (`redirect-job-to-alert.resolver.ng1.ts:43`). Upstream navigates with
                 * `extensionJobId` and lets a RESOLVER turn it into an `alertId`; there is no
                 * resolver layer here, so the same two steps happen in the handler.
                 *
                 * ⚠️ LOOK BEFORE CONVERTING. The resolver searches the alerts already loaded
                 * for one whose `extensionJobId` matches and only calls `convertJobToAlert`
                 * when there is none — because that call CREATES an alert. Converting on every
                 * click would make a duplicate alert each time a job row is opened.
                 *
                 * This was left unpassed while the conversion was unported, which is why
                 * clicking a job row did nothing while a "My searches" row worked.
                 */
                // See `openJob`.
                onSelectJob={(job) => {
                  setPanelOpen(false)
                  openJob(job)
                }}
              />
                  ),
                }
              : {})}
            {...(criteriaOpen
              ? {
                  criteriaPanel: (
                    <FriendlyCriteriaPanel
                      {...(criteriaData === undefined ? {} : { data: criteriaData })}
                      fieldOfExpertiseOperator={
                        (current.alert.extensionCriteria?.['fieldOfExpertiseOperator'] as
                          | LogicalOperator
                          | null) ?? null
                      }
                      // `localeService.getTimeSince` — the long form, which is what the
                      // panel's two date lines read ("3 months ago", not "3mos ago").
                      timeSince={(epoch) => (epoch === undefined || epoch === null ? '' : timeSince(epoch))}
                      onDismiss={() => {
                        setCriteriaOpen(false)
                      }}
                    />
                  ),
                }
              : {})}
            checkboxDependent={checkboxDependent}
            {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
          />
          {multiSelectAction === undefined || current === undefined ? null : (
            <MultiSelectModal
              onSelectAllInPage={() => {
                selectAllInPage(current.alert.id)
              }}
              onSelectAllInResults={selectAllInResults}
              getCheckedCount={() => getCheckedEntities(current.alert.id).length}
              onClose={() => {
                // `verifyHasSelectedCandidates` runs the callback either way; the callback's own
                // empty check is what stops a dismissed modal.
                const action = multiSelectAction
                setMultiSelectAction(undefined)
                runSelectedAction(current.alert, action)
              }}
            />
          )}
          {!noValidCandidatesOpen ? null : (
            <NoValidCandidatesDialogue
              onClose={() => {
                setNoValidCandidatesOpen(false)
              }}
            />
          )}
          {exportingToAts === undefined || current === undefined ? null : (
            // ⚠️ MOUNTED UNTIL `onClose`, NOT UNTIL THE ✕: a hidden dialogue keeps polling, and
            // its success is what records the export (see the component).
            <ExportToAtsModal
              entity="candidates"
              alertId={current.alert.id}
              alertName={current.alert.name ?? ''}
              ids={exportingToAts}
              {...(atsName === undefined ? {} : { atsName })}
              onExported={(ids) => {
                onCandidatesExported(current.alert.id, ids, true)
                trackCandidateBulk.exported(current.alert.id, ids, candidates ?? [])
              }}
              onOpenInAts={sendOpenHotlist}
              onClose={() => {
                setExportingToAts(undefined)
              }}
            />
          )}
          {exportingToCsv === undefined || current === undefined ? null : (
            <ExportToCsvModal
              alertId={current.alert.id}
              alertName={current.alert.name ?? ''}
              candidateIds={exportingToCsv}
              onExported={(ids) => {
                onCandidatesExported(current.alert.id, ids, false)
              }}
              onClose={() => {
                setExportingToCsv(undefined)
              }}
            />
          )}
          {sendingDetailsFor === undefined ? null : (
            <ProfileActionConfirmationModal
              inProgressMessage={CANDIDATE_DETAILS_IN_PROGRESS_MESSAGE}
              okButtonText={PROFILE_ACTION_OK_BUTTON_TEXT}
              run={() => candidateDetailsResultMessage(() => sendCandidateDetails(sendingDetailsFor))}
              onClose={() => {
                setSendingDetailsFor(undefined)
              }}
            />
          )}
          {deleting === undefined ? null : (
            <DeleteAlertDialogue
              alertName={deleting.name}
              onConfirm={confirmDelete}
              onCancel={() => {
                setDeleting(undefined)
              }}
            />
          )}
          {!activatingAutomation || current === undefined ? null : (
            <ActivateAutomationDialogue
              alertName={current.alert.name ?? ''}
              atsLabel={atsLabel ?? ''}
              // `extensionTabData.totalResults` — the number the copy quotes, and the number
              // of people this will mail.
              totalResults={tabData[current.alert.id]?.totalResults ?? totalResults ?? 0}
              onConfirm={onConfirmAutomation}
              onCancel={() => {
                setActivatingAutomation(false)
              }}
            />
          )}
          {/* ⚠️ FOUR CONDITIONS, and one of them is deliberately absent. Upstream renders this
              when `currentViewMode !== FullMode && shortlistSessionInfo.inATS && !!response &&
              response.configurations.length > 0`. The frame is always slim here, the response
              and its length are checked — but `inATS` means the extension is framed inside an
              ATS page rather than opened standalone, which is a property of the SESSION and
              arrives with the shell's messaging (Phase 6.3). Until it does, this renders
              whenever the tenant HAS sources, which is a superset of upstream: a user who
              opened the frame outside their ATS would see a selector production hides. That is
              recorded rather than papered over, because guessing `inATS` from the URL would be
              a rule this port invented. */}
          {atsName !== undefined && (sources?.configurations.length ?? 0) > 0 ? (
            <SourcesDropdown
              options={sourceOptions(
                atsName,
                (sources?.configurations ?? []).map((configuration) => configuration.hostSite),
                atsLabel ?? atsName,
              )}
              open={sourcesOpen}
              // `currentExternalSource` — the trigger shows the chosen source, and the chosen
              // ROW gets the tick. Reading it off the URL is what `$stateParams.externalSource`
              // is; without it the trigger always read the ATS name however the list was drawn.
              {...(routeParams.get('externalSource') === null
                ? {}
                : { current: routeParams.get('externalSource') as SocialHost })}
              onToggle={() => setSourcesOpen((open) => !open)}
              // ⚠️ `onSourceClick` → `moveToExternalSource`
              // (`shortlist-extension-alerts.component.ng1.ts:356`): the ATS row goes to the
              // plain review and every other row to `external-source-review` with the host in
              // the URL. Choosing the row you are already on returns early. This was left
              // unpassed deliberately while the screen did not read `?externalSource`; it does
              // now, and leaving it inert meant picking Monster changed nothing at all.
              onSelect={(option) => {
                setSourcesOpen(false)
                const chosen = option.type
                const currentSource = routeParams.get('externalSource')
                if ((chosen ?? null) === (currentSource ?? null)) return
                const query = new URLSearchParams({ alertId: String(current.alert.id) })
                if (chosen === null) {
                  void navigate(`/chrome-extension/extension/shortlist/review?${query.toString()}`)
                  return
                }
                query.set('externalSource', chosen)
                void navigate(
                  `/chrome-extension/extension/shortlist/external-source-review?${query.toString()}`,
                )
              }}
            />
          ) : null}
          {/* The panel waits for the FLAG, not just for the alerts: it selects between two
              entirely different forms, and opening the wrong one is worse than opening none.
              `automatedHunters === undefined` means the bootstrap has not answered yet or
              could not be read. */}
          {/* ⚠️ THE TWO WRAPPERS ARE NOT DECORATION — `.extension-alert-candidates-slim-container`
              is the ROOT SELECTOR of `_extension-alert-candidates-slim.scss`. Without it every
              rule in that 784-line file matches nothing, and the rows render as unstyled text
              in a column with no width: the pixel diff was 32% before this div existed and
              3.7% after. The inner
              `.extension-alert-candidates-slim-filters-and-selection` groups the three bars
              that stay put while the list scrolls, which is why the list has a scroll
              container of its own below it. Both are
              `extension-alert-candidates-slim.tpl.html:1-2`. */}
          <AlertContainerHost>
            <CandidateListHost entity="candidates" loading={candidates === undefined}>
              <div className="extension-alert-candidates-slim-container">
                <div className="extension-alert-candidates-slim-filters-and-selection">
                  {/* ⚠️ THE BANNER COMES FIRST, ABOVE SORT/FILTERS. Upstream's order is
                  automation (line 4), then filters (line 25), then selection (line 84), and
                  reading the template's sections in the order they are easiest to port puts
                  the banner in the middle of the three. */}
                  {/* `ng-if="::ctrl.shortlistHunterAutomationService.isEnabled"` — the SERVICE
                      flag, not `AUTOMATED_HUNTERS`. */}
                  {automationEnabled ? (
                    <HunterAutomationBanner
                      atsName={atsName}
                      automationMode={current.alert.automationMode}
                      // ⚠️ THE BUTTON OPENS THE CONFIRMATION, IT DOES NOT CALL THE API.
                      // `enableAutomation` mails every matching candidate and keeps mailing
                      // new ones — see the note on the API function. Upstream's own flow is
                      // dialogue first, `enableAutomation` only on confirm.
                      onActivate={() => {
                        setActivatingAutomation(true)
                      }}
                      onToggle={onToggleAutomation}
                    />
                  ) : null}
                  {/* ⚠️ SORT IS LIVE; THE SHOWN/CV-DATE MENU IS NOT PORTED YET, AND THE TWO ARE
                  DIFFERENT KINDS OF GAP. `onSelectSort` re-queries, which is upstream's
                  `changeSortOrder` → `startNewSearch()`. The other two have no menu to open —
                  `extension-alert-candidates-filters.tpl.html` is a tooltip this port has not
                  taken — so they are DISPLAYED from the alert and SENT with the query, but
                  cannot be changed here. That is the honest shape: the bar now tells the truth
                  about what the list contains, which it did not when it rendered
                  `SHOWN_OPTION_DEFAULT` over an alert the recruiter had left on "Archived". */}
                  <CandidateFiltersBar
                    currentSort={sortOption.display}
                    shownOptionName={shownOption.name}
                    cvDateOptionName={cvDateOption.name ?? CV_DATE_OPTION_DEFAULT}
                    sortOpen={sortOpen}
                    onToggleSort={() => {
                      setSortOpen((open) => !open)
                    }}
                    onToggleFilters={() => {
                      setFiltersOpen((open) => !open)
                    }}
                    filtersMenu={
                      filtersOpen ? (
                        <CandidateFiltersTooltip
                          shownOption={shownOption}
                          cvDateOption={cvDateOption}
                          shownOptions={shownOptionsFor({ ...actionFlags, actionedAndUnactionedEnabled: actionedViewEnabled })}
                          groupOpen={groupOpen}
                          cvDateOpen={cvDateOpen}
                          onToggleGroup={() => {
                            setGroupOpen((open) => !open)
                          }}
                          onToggleCvDate={() => {
                            setCvDateOpen((open) => !open)
                          }}
                          onSelectShown={(option) => {
                            // `changeShownCandidates` closes the tooltip first
                            // (`onFiltersTooltipClose`), then re-queries.
                            closeFiltersMenu()
                            // ⚠️ RECORDED BEFORE THE NO-OP CHECK: upstream's handler has no such
                            // check, so re-picking the option on display is still a click it logs.
                            trackCandidateList.shownFilter(current.alert.id, option.name)
                            if (option.name === shownOption.name) {
                              // …and upstream searches again, which this screen skips.
                              recordCandidateSearch(current.alert.id, sortOption.field, buildCandidateFilter(shownOption, cvDateOption.value), true)
                              return
                            }
                            setShownOption(option)
                            setFiltersChosen(true)
                          }}
                          onSelectCvDate={(option) => {
                            closeFiltersMenu()
                            trackCandidateList.relevancyDate(current.alert.id)
                            if (option.name === cvDateOption.name) {
                              recordCandidateSearch(current.alert.id, sortOption.field, buildCandidateFilter(shownOption, cvDateOption.value), true)
                              return
                            }
                            setCvDateOption(option)
                            setFiltersChosen(true)
                          }}
                        />
                      ) : null
                    }
                    onSelectSort={(option) => {
                      setSortOpen(false)
                      if (option.field === sortOption.field) return
                      // The bar is shared with the contacts screen, so it hands back the
                      // common shape; this screen's own option is the one with the enum.
                      const chosen = SORT_OPTIONS.find((candidate) => candidate.field === option.field)
                      if (chosen === undefined) return
                      setSortOption(chosen)
                      setSortChosen(true)
                    }}
                    totalResults={totalResults}
                    checkboxDependent={checkboxDependent}
                    {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
                  />
                  {/* `extension-alert-candidates-slim.tpl.html:73` — the Message History's
                      anchor sits HERE, between the bars, not on the row. */}
                  <MessageHistoryTooltip {...messageHistory} />
                  {/* ⚠️ THE ACTION LIST IS BUILT FROM FLAGS THIS SCREEN DOES NOT HAVE YET, so it
                  comes out nearly empty — `addToAtsJobEnabled`, `exportCandidatesEnabled` and
                  the rest live on services that arrive with the messaging and export layers.
                  What it DOES get right is the alert's origin, which is the condition most
                  likely to be misread: only a LinkedIn import loses Favor, Archive and Export.
                  Passing the alert now means the bar is wrong in quantity rather than in
                  kind. */}
                  {candidates === undefined ? null : (
                    <CandidateSelectionBar
                      // ⚠️ THE SHOWN FILTER IS MERGED IN HERE, NOT STORED IN `actionFlags`.
                      // `bulkActions` hides Favor on the Favorites filter and Archive on
                      // Archived (`bulkActions.ts:145,152`), so the value has to be the one
                      // the bar is currently showing — holding it in the view-model state,
                      // which is fetched once, would freeze it at whatever the alert opened
                      // with and leave Archive offered on the Archived list.
                      actions={bulkActions(current.alert, { ...actionFlags, shownOptionName: shownOption.name })}
                      selectedCount={selectedIds.size}
                      selectMenuOpen={selectMenuOpen}
                      onToggleSelectMenu={() => {
                        setSelectMenuOpen((open) => !open)
                      }}
                      onSelectAllInPage={() => {
                        setSelectMenuOpen(false)
                        // ⚠️ UPSTREAM ALSO MARKS THE PAGE VIEWED HERE (`viewCandidates(this.page)`)
                        // AND THIS DOES NOT. That is a write against the recruiter's real data —
                        // a viewed candidate drops out of the "Unviewed" filter — and selecting
                        // rows to act on them is not the same gesture as having read them. It
                        // lands with the single-action writes, which this screen also withholds.
                        // `viewCandidates(this.page)` — the PAGE, not the whole result set.
                        // While the list rendered everything these were the same thing; now
                        // they are not, and "select all in page" would otherwise tick 433.
                        selectAllInPage(current.alert.id)
                      }}
                      onSelectAllInResults={() => {
                        setSelectMenuOpen(false)
                        void selectAllInResults()
                      }}
                      // The overflow caret had state and a handler declared and nothing passed,
                      // so the third and later bulk actions were unreachable — `onAction` below
                      // already does the work for them.
                      overflowOpen={bulkOverflowOpen}
                      onToggleOverflow={() => {
                        setBulkOverflowOpen((open) => !open)
                      }}
                      onAction={(action) => {
                        setBulkOverflowOpen(false)
                        onBulkAction(action)
                      }}
                      onClearAll={() => {
                        setSelectMenuOpen(false)
                        // `resetCandidatesCheckbox` — the only thing that empties it.
                        setSelectedIds(new Set())
                        trackCandidateSelection.multi(current.alert.id, 'clearAllInPage', 0)
                      }}
                      checkboxDependent={checkboxDependent}
                      {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
                    />
                  )}
                </div>
                {/* The list's own scroll container. The three classes after it are upstream's
                scrollbar cosmetics (`nicer-scroller mac-style four-px-scroll`) and the id is
                what upstream scrolls to the top of after a new search. */}
                <div
                  className="candidate-lister-container-slim nicer-scroller mac-style four-px-scroll"
                  id="results-slim-scrollable-container"
                >
                  {candidates === undefined ? null : (
                    <div className="ext-alerts-results-slim-wrap">
                      <div className="ext-alerts-results-slim">
                        {(pageCandidates ?? []).map((candidate) => (
                          <CandidateRow
                            key={candidate.id}
                            candidate={candidate}
                            messagingEnabled={messagingEnabled}
                            onOpenMessages={() => {
                              messageHistory.openFor(candidate.id)
                            }}
                            showAlgorithm={showAlgorithm}
                            now={renderedAt}
                            checked={selectedIds.has(candidate.id)}
                            previouslySelected={openCandidateId === candidate.id}
                            onOpen={() => {
                              openCandidate(candidate.id, candidate.algorithm)
                            }}
                            // `previewCandidateResume` — the chevron and the row reach the
                            // same place upstream, because the row's handler calls it.
                            onPreviewResume={() => {
                              openCandidate(candidate.id, candidate.algorithm)
                            }}
                            onCheck={(checked) => {
                              setSelectedIds((previous) => {
                                const next = new Set(previous)
                                if (checked) next.add(candidate.id)
                                else next.delete(candidate.id)
                                return next
                              })
                              if (externalSource === null) trackCandidateSelection.checked(current.alert.id, candidate, checked)
                            }}
                            // The six-icon hover strip. `ProfileActionsStrip` has been ported
                            // since the snippets landed; the row simply never rendered it, so
                            // hovering drew the highlight over an empty space where prod shows
                            // download, add-to-job, message, share, favour and archive.
                            // ⚠️ THE STRIP IS THE ATS LIST'S. Its six actions all act on an
                            // alert-candidate id; a source's rows carry an external string id
                            // and their own actions (expose, add to ATS), which are the
                            // external-source review's own port.
                            {...(externalSource === null ? { actions: rowActions } : {})}
                            onAction={(action) => {
                              if (externalSource === null) onRowAction(candidate as AlertCandidate, action)
                            }}
                            checkboxDependent={checkboxDependent}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* The note that stood here said the page size was not knowable yet. It is a
                  constant upstream — `private readonly pageSize: number = 10` — and the pager
                  rendered "1 / 1" over a list of every candidate in the alert. */}
                  {candidates === undefined || !showPager ? null : (
                    <div className="extension-alert-candidate-slim-pagination">
                      <CandidatePagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        onMove={(delta) => {
                          // `movePage` refuses to leave the range; `CandidatePagination`
                          // already checks, and this clamps so a stale `lastPage` cannot
                          // strand the list on an empty page.
                          const page = Math.min(Math.max(currentPage + delta, 1), lastPage)
                          setCurrentPage(page)
                          // `scrollTop()` — upstream returns to the top of the list on every
                          // page move, or page 2 opens halfway down.
                          document.getElementById('results-slim-scrollable-container')?.scrollTo({ top: 0 })
                          // `persistState(candidateId, …)` in `movePage`. With no preview open
                          // there is no row; with one, upstream opens the new page's first row
                          // and this screen keeps the open one, so that stays the selection.
                          selectedRow.current = openCandidateId === undefined ? undefined : selectedRow.current
                          persistListState(selectedRow.current, page)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CandidateListHost>
          </AlertContainerHost>
        </div>
      </ExtensionAlertsHost>
      {/* ⚠️ ONE CLOCK FOR THE WHOLE LIST. Every row renders a relative CV date, and reading
          `Date.now()` per row means two rows a millisecond apart can disagree across a minute
          boundary — which a pixel diff would catch as a one-character difference on one row and
          nothing else. It is also what makes the rows deterministic under capture. */}
      {/* ⚠️ THE BANNER IS BEHIND THE TENANT FLAG, NOT THE ALERT'S MODE. Upstream's
          `ng-if="::shortlistHunterAutomationService.isEnabled"` asks whether automation exists
          for this WHITELABEL; the alert's `automationMode` then chooses between the activate
          button and the switch. Conflating them would hide the banner on every alert that has
          never been automated, which is exactly the alert the button is for.

          `automatedHunters` is the same bootstrap flag the dropdown reads. It is the closest
          thing available: upstream's service reads it too (entity-hunters-container-base
          .ng1.ts:260) before layering its own checks on top, and those need the shortlist
          services that have not landed. */}
      {/* The side view, empty until a candidate profile can open in it (slice 4). The div is
          NOT `rendered`, which is the class upstream adds only when a child state is active —
          so it contributes the same zero-width box production gives it here. */}
      <div className={`shortlist-hunter-review-side-view${sideView === null ? '' : ' rendered'}`}>{sideView}</div>
    </div>
  )
}
